import React, {useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {EmpWebPushRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {isIos, isStandalone} from "../../../components/utils/install_prompt";
import {loadPushStatus, subscribePush, supported} from "./push_toggle";

/** Cada cuánto se vuelve a preguntar en un dispositivo que dijo «Ahora no». */
const ASK_EVERY_DAYS = 90;
const KEY = "arteco.push.asked";

const lastAsked = (): number | undefined => {
	try {
		const v = window.localStorage.getItem(KEY);
		return v ? Number(v) : undefined;
	} catch {
		return undefined;
	}
};

const markAsked = () => {
	try {
		window.localStorage.setItem(KEY, String(Date.now()));
	} catch {
		// sin almacenamiento (navegación privada): se volverá a preguntar, que es lo menos malo
	}
};

/**
 * Pregunta si se activan los avisos de fichaje en este dispositivo cuando no lo están, y si se dice
 * «Ahora no», no vuelve a hacerlo en 90 días (#13811). Es un diálogo propio porque iOS solo deja pedir
 * el permiso desde un clic. No sale si el navegador ya lo tiene denegado (no se puede volver a pedir),
 * ni en el Safari del iPhone sin instalar la app, donde el push no existe.
 */
export const PushAsk = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EmpWebPushRestControllerApiFactory);
	const [publicKey, setPublicKey] = useState<string>();
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!supported() || (isIos() && !isStandalone()) || Notification.permission === "denied") {
			return;
		}
		const last = lastAsked();
		if (last && Date.now() - last < ASK_EVERY_DAYS * 24 * 3600 * 1000) {
			return;
		}
		loadPushStatus(apiManager, ctrl)
			.then(r => {
				if (r.status?.enabled && !(r.status.subscribed && r.endpoint)) {
					setPublicKey(r.status.publicKey || "");
				}
			})
			.catch(() => undefined);
	}, []);

	if (publicKey === undefined) {
		return null;
	}

	const close = () => {
		markAsked();
		setPublicKey(undefined);
	};

	const activate = async () => {
		setBusy(true);
		try {
			await subscribePush(apiManager, ctrl, publicKey);
		} catch {
			// la tarjeta de avisos sigue ahí para intentarlo a mano
		} finally {
			setBusy(false);
			close();
		}
	};

	return (
		<Dialog open onClose={close} maxWidth="xs" fullWidth>
			<DialogTitle fontWeight={700}>¿Activar los avisos de fichaje?</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary">
					Si se te olvida fichar, te llega una notificación en este dispositivo en lugar del correo.
					Puedes desactivarlo cuando quieras en «Registro de jornada».
				</Typography>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2}}>
				<Button color="inherit" onClick={close} disabled={busy} sx={{textTransform: "none"}}>Ahora no</Button>
				<Button variant="contained" disableElevation onClick={activate} disabled={busy}
						sx={{textTransform: "none", fontWeight: 600}}>
					Activar avisos
				</Button>
			</DialogActions>
		</Dialog>
	);
};
